import { DataFeedEntity, DataFeedStage, DataFeedUpload } from '../../models/data-feed.model';

/** Display metadata for a status badge: text, Material icon and the CSS tone class. */
export interface BadgeMeta {
  label: string;
  icon: string;
  tone: 'waiting' | 'processing' | 'success' | 'warning' | 'failed' | 'neutral' | 'paused' | 'cancelled';
  spin?: boolean;
}

const STAGE_META: Record<DataFeedStage, BadgeMeta> = {
  AwaitingConfirmation: { label: 'To confirm', icon: 'fact_check', tone: 'paused' },
  Uploaded: { label: 'Uploaded', icon: 'cloud_upload', tone: 'waiting' },
  Queued: { label: 'Queued', icon: 'schedule', tone: 'waiting' },
  Processing: { label: 'Processing', icon: 'autorenew', tone: 'processing', spin: true },
  Completed: { label: 'Completed', icon: 'check_circle', tone: 'success' },
  CompletedWithErrors: { label: 'Completed with errors', icon: 'warning', tone: 'warning' },
  Rejected: { label: 'Rejected', icon: 'block', tone: 'failed' },
  Failed: { label: 'Failed', icon: 'error', tone: 'failed' },
  Paused: { label: 'Paused', icon: 'pause_circle', tone: 'paused' },
  Cancelled: { label: 'Cancelled', icon: 'cancel', tone: 'cancelled' },
  Discarded: { label: 'Discarded', icon: 'delete', tone: 'cancelled' },
};

const ENTITY_STATUS_META: Record<string, BadgeMeta> = {
  pending: { label: 'Pending', icon: 'schedule', tone: 'neutral' },
  inprogress: { label: 'In progress', icon: 'autorenew', tone: 'processing', spin: true },
  completed: { label: 'Completed', icon: 'check_circle', tone: 'success' },
  failed: { label: 'Failed', icon: 'error', tone: 'failed' },
  // Was loading when the import was paused / cancelled: static, not spinning.
  paused: { label: 'Paused', icon: 'pause_circle', tone: 'paused' },
  cancelled: { label: 'Cancelled', icon: 'cancel', tone: 'cancelled' },
};

/** Timeline events with their own icon / colour (others fall back to their severity). */
const EVENT_TYPE_META: Record<string, BadgeMeta> = {
  uploadstaged: { label: 'Upload staged', icon: 'upload_file', tone: 'waiting' },
  uploadconfirmed: { label: 'Upload confirmed', icon: 'fact_check', tone: 'success' },
  uploaddiscarded: { label: 'Upload discarded', icon: 'delete', tone: 'neutral' },
  uploadrejected: { label: 'Upload rejected', icon: 'block', tone: 'failed' },
  pauserequested: { label: 'Pause requested', icon: 'pending', tone: 'paused' },
  paused: { label: 'Paused', icon: 'pause_circle', tone: 'paused' },
  cancelrequested: { label: 'Cancel requested', icon: 'pending', tone: 'cancelled' },
  cancelled: { label: 'Cancelled', icon: 'cancel', tone: 'cancelled' },
  resumerequested: { label: 'Resume requested', icon: 'pending', tone: 'processing' },
  resumed: { label: 'Resumed', icon: 'play_circle', tone: 'processing' },
  resumefailed: { label: 'Resume failed', icon: 'error', tone: 'failed' },
};

const SEVERITY_META: Record<string, BadgeMeta> = {
  info: { label: 'Info', icon: 'info', tone: 'processing' },
  warning: { label: 'Warning', icon: 'warning', tone: 'warning' },
  error: { label: 'Error', icon: 'error', tone: 'failed' },
};

export const ACTIVE_STAGES: ReadonlySet<DataFeedStage> = new Set<DataFeedStage>(['AwaitingConfirmation', 'Uploaded', 'Queued', 'Processing']);

/** A person as the monitor shows them: the display name when known, else the e-mail; with `withEmail`, "Name (e-mail)". */
export function person(name: string | null | undefined, email: string | null | undefined, withEmail = false): string {
  const n = (name || '').trim();
  const e = (email || '').trim();
  if (n && e && withEmail && n.toLowerCase() !== e.toLowerCase()) { return `${n} (${e})`; }
  return n || e;
}

export function isActiveStage(stage: DataFeedStage | null | undefined): boolean {
  return !!stage && ACTIVE_STAGES.has(stage);
}

export function stageMeta(stage: DataFeedStage | string | null | undefined): BadgeMeta {
  return STAGE_META[stage as DataFeedStage] ?? { label: stage || 'Unknown', icon: 'help_outline', tone: 'neutral' };
}

export function entityStatusMeta(status: string | null | undefined): BadgeMeta {
  const key = (status || '').toLowerCase();
  return ENTITY_STATUS_META[key] ?? { label: status || 'Unknown', icon: 'help_outline', tone: 'neutral' };
}

/** Generic status (IA steps, dead letters, validation) mapped onto a tone by keyword. */
export function genericStatusMeta(status: string | null | undefined): BadgeMeta {
  const s = (status || '').toLowerCase();
  if (!s) { return { label: '-', icon: 'remove', tone: 'neutral' }; }
  if (/(fail|error|invalid|reject|dead)/.test(s)) { return { label: status!, icon: 'error', tone: 'failed' }; }
  if (/(warn|partial)/.test(s)) { return { label: status!, icon: 'warning', tone: 'warning' }; }
  if (/(progress|running|processing|pending|open|queued)/.test(s)) { return { label: status!, icon: 'schedule', tone: 'processing' }; }
  if (/(complete|success|succeeded|valid|passed|done|replayed|resolved|ok)/.test(s)) { return { label: status!, icon: 'check_circle', tone: 'success' }; }
  return { label: status!, icon: 'radio_button_unchecked', tone: 'neutral' };
}

/** Icon / tone for a timeline event: event-type specific when known, otherwise by severity. */
export function eventMeta(eventType: string | null | undefined, severity: string | null | undefined): BadgeMeta {
  return EVENT_TYPE_META[(eventType || '').toLowerCase()] ?? severityMeta(severity);
}

export function severityMeta(severity: string | null | undefined): BadgeMeta {
  return SEVERITY_META[(severity || '').toLowerCase()] ?? { label: severity || 'Info', icon: 'info', tone: 'neutral' };
}

/** Human readable duration: 850 ms, 12s, 3m 05s, 1h 02m. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || isNaN(ms) || ms < 0) { return '-'; }
  if (ms < 1000) { return `${Math.round(ms)} ms`; }
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) { return `${hours}h ${pad(minutes)}m`; }
  if (minutes > 0) { return `${minutes}m ${pad(seconds)}s`; }
  return `${seconds}s`;
}

/** Compact count for summary cards: 950, 12.7K, 3.4M (exact value goes in a tooltip). */
export function formatCompact(value: number | null | undefined): string {
  const n = value ?? 0;
  if (!isFinite(n)) { return '0'; }
  const abs = Math.abs(n);
  if (abs < 10000) { return n.toLocaleString('en-US'); }
  const units: Array<[number, string]> = [[1e9, 'B'], [1e6, 'M'], [1e3, 'K']];
  for (const [size, suffix] of units) {
    if (abs >= size) {
      const scaled = n / size;
      const text = Math.abs(scaled) >= 100 ? Math.round(scaled).toString() : (Math.round(scaled * 10) / 10).toString();
      return `${text}${suffix}`;
    }
  }
  return n.toLocaleString('en-US');
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function toTime(value: string | null | undefined): number | null {
  if (!value) { return null; }
  const t = Date.parse(value);
  return isNaN(t) ? null : t;
}

/** Elapsed run time; ticks live (from StartedAt to now) while the run is processing. */
export function uploadDurationMs(upload: DataFeedUpload, now: number): number | null {
  const started = toTime(upload.StartedAt);
  if (upload.Stage === 'Processing' && started !== null) {
    return Math.max(0, now - started);
  }
  if (upload.DurationMs !== null && upload.DurationMs !== undefined) {
    return upload.DurationMs;
  }
  const completed = toTime(upload.CompletedAt);
  return started !== null && completed !== null ? Math.max(0, completed - started) : null;
}

export function entityDurationMs(entity: DataFeedEntity, now: number): number | null {
  if (entity.DurationMs !== null && entity.DurationMs !== undefined) { return entity.DurationMs; }
  const started = toTime(entity.StartedAt);
  if (started === null) { return null; }
  const completed = toTime(entity.CompletedAt);
  if (completed !== null) { return Math.max(0, completed - started); }
  return (entity.Status || '').toLowerCase() === 'inprogress' ? Math.max(0, now - started) : null;
}

/** Percentage of an entity's records that have been handled (0-100), or null when unknown. */
export function entityProgress(entity: DataFeedEntity): number | null {
  const status = (entity.Status || '').toLowerCase();
  if (status === 'completed' || status === 'failed') { return 100; }
  // null means "indeterminate"; only an entity that is still loading may animate.
  if (!entity.Total || entity.Total <= 0) { return status === 'pending' || status === 'paused' || status === 'cancelled' ? 0 : null; }
  const handled = (entity.Succeeded || 0) + (entity.Failed || 0) + (entity.Skipped || 0);
  return Math.min(100, Math.round((handled / entity.Total) * 100));
}

/** Display name of an upload for messages and confirmations. */
export function uploadName(upload: DataFeedUpload | null | undefined): string {
  return upload?.FileName || upload?.DisplayName || upload?.RunId || 'this upload';
}

/** True while a pause, cancel or resume request is waiting for the worker to act on it. */
export function hasPendingControl(upload: DataFeedUpload | null | undefined): boolean {
  return !!upload && (!!upload.StopRequestedAt || !!upload.ResumeRequestedAt);
}

/** Chip shown next to the stage while a pause / cancel / start request is pending. */
export interface PendingControlMeta {
  label: string;
  tone: BadgeMeta['tone'];
  tooltip: string;
}

export function pendingControlMeta(upload: DataFeedUpload | null | undefined): PendingControlMeta | null {
  if (!upload) { return null; }
  const by = (who: string | null | undefined) => (who ? ` by ${who}` : '');
  if (upload.StopRequestedAt) {
    return upload.StopRequestedMode === 'Cancel'
      ? { label: 'Stopping\u2026', tone: 'cancelled', tooltip: `Stop requested${by(upload.StopRequestedBy)}; cancelling the import` }
      : { label: 'Pausing\u2026', tone: 'paused', tooltip: `Pause requested${by(upload.StopRequestedBy)}; pausing the import` };
  }
  if (upload.ResumeRequestedAt) {
    return { label: 'Starting\u2026', tone: 'processing', tooltip: `Start requested${by(upload.ResumeRequestedBy)}; waiting for the worker to queue it again` };
  }
  return null;
}

/** Keeps a server error readable in a snack bar or banner: first line only, no stack trace, capped length. */
export function shortError(message: string, max = 240): string {
  const firstLine = (message || '').split(/\r?\n/)[0].replace(/\s+at\s+\S.*$/, '').trim();
  return firstLine.length > max ? `${firstLine.slice(0, max - 1)}\u2026` : firstLine;
}

/** Hands a downloaded blob to the browser as a file. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/** "updated 2s ago" style relative time. */
export function relativeTime(from: number | null, now: number): string {
  if (from === null) { return 'never'; }
  const s = Math.max(0, Math.round((now - from) / 1000));
  if (s < 5) { return 'just now'; }
  if (s < 60) { return `${s}s ago`; }
  const m = Math.floor(s / 60);
  if (m < 60) { return `${m}m ago`; }
  return `${Math.floor(m / 60)}h ago`;
}

/** Pretty-prints JSON text; falls back to the raw text when it is not valid JSON. */
export function prettyJson(text: string | null | undefined): string {
  if (text === null || text === undefined || text === '') { return ''; }
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/** Parses JSON text safely; returns the raw text when it is not valid JSON. */
export function safeParse(text: string | null | undefined): unknown {
  if (text === null || text === undefined || text === '') { return null; }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Short, human friendly label for an HTTP error. */
export function describeHttpError(err: any, fallback: string): string {
  if (!err) { return fallback; }
  if (err.status === 0) { return 'Cannot reach the server. Check your connection.'; }
  if (err.status === 404) { return 'Not found.'; }
  if (err.status === 401 || err.status === 403) { return 'You are not authorised to view this.'; }
  const msg = err?.error?.message || err?.error?.Message || (typeof err?.error === 'string' ? err.error : null);
  return msg ? `${fallback} (${msg})` : `${fallback}${err.status ? ` (HTTP ${err.status})` : ''}`;
}
